import React, { ReactNode, useEffect, useState } from 'react';
import { Alert, ListGroup, Spinner } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import PageWrapper from 'components/PageWrapper';
import DocsApi from 'services/KjederegisteretAdminApi/DocsApi';
import { Dokument, DokumentInfo } from 'services/KjederegisteretAdminApi/DocsApi/types';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import DokumentStatus from './DokumentStatus';
import MarkdownDocument from './MarkdownDocument';

const DokumentListe = () => {
  const [dokumenter, setDokumenter] = useState<DokumentInfo[] | undefined>();
  const [feil, setFeil] = useState<ReactNode>();

  useEffect(() => {
    DocsApi.list()
      .then(setDokumenter)
      .catch((e) => setFeil(HttpErrorHelper.formatError(e)));
  }, []);

  if (feil) return <Alert variant="danger">{feil}</Alert>;
  if (!dokumenter) return <Spinner animation="border" role="status" />;
  if (dokumenter.length === 0) return <p>Ingen dokumenter er publisert ennå.</p>;

  return (
    <ListGroup>
      {dokumenter.map((d) => (
        <ListGroup.Item key={d.slug} className="d-flex justify-content-between align-items-center">
          <Link to={`/docs/${d.slug}`}>{d.tittel}</Link>
          <DokumentStatus info={d} />
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
};

const DokumentVisning = ({ slug }: { slug: string }) => {
  const { hash } = useLocation();
  const [dokument, setDokument] = useState<Dokument | undefined>();
  const [feil, setFeil] = useState<ReactNode>();

  useEffect(() => {
    setDokument(undefined);
    setFeil(undefined);
    DocsApi.get(slug)
      .then(setDokument)
      .catch((e) =>
        setFeil(
          e?.response?.status === 404 ? 'Dokumentet finnes ikke.' : HttpErrorHelper.formatError(e),
        ),
      );
  }, [slug]);

  // Hopp til ankeret (#...) når dokumentet er lastet
  useEffect(() => {
    if (dokument && hash)
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
  }, [dokument, hash]);

  if (feil) return <Alert variant="danger">{feil}</Alert>;
  if (!dokument) return <Spinner animation="border" role="status" />;

  return (
    <>
      <p>
        <Link to="/docs">← Alle dokumenter</Link>
      </p>
      <p>
        <DokumentStatus info={dokument.info} />
      </p>
      <MarkdownDocument slug={slug} markdown={dokument.markdown} />
    </>
  );
};

const Docs = () => {
  const { slug } = useParams();
  return (
    <PageWrapper header="Dokumentasjon">
      {slug ? <DokumentVisning slug={slug} /> : <DokumentListe />}
    </PageWrapper>
  );
};

export default Docs;
